/**
 * Reference lint — atomization safety net (Step 1).
 *
 * The per-kind file filters in {@link ./builtin-providers.ts} decide which
 * files belong to ONE asset purely structurally (manifest dir + a fixed set of
 * conventional companion files/subdirs). They never read the manifest *body*,
 * so a manifest that textually points at a helper file living in a
 * non-conventional location (e.g. a skill referencing `modes/write.md` or
 * `scripts/run.ts`) silently drops those bytes from the atomized asset.
 *
 * This module scans the *text* of the already-included files for path-like
 * references, resolves each against the asset root, and classifies it. The
 * `uncovered-in-dir` class is the actionable one: a real file or directory that
 * lives inside the asset's own directory, is referenced by the asset, yet was
 * NOT captured by the structural filter. Discovery surfaces those as warnings.
 *
 * Pure classification — no auto-inclusion here. Closure (Step 3) and the
 * full-dir fallback (Step 4) build on the same scan.
 */

import * as fs from "node:fs";
import * as path from "node:path";
import type { RequiresEdge } from "./requires-graph.js";

/**
 * How a textual path reference relates to the asset's captured byte set.
 *
 * - `covered`           — resolves to a file/dir already in the asset's filter.
 * - `uncovered-in-dir`  — resolves to an existing file/dir INSIDE the asset
 *                         root that the structural filter dropped (actionable).
 * - `external`          — resolves outside the asset root (cross-asset / repo
 *                         file); not this asset's bytes to bundle.
 * - `unresolved`        — points at nothing on disk (prose / false positive).
 */
export type ReferenceClass = "covered" | "uncovered-in-dir" | "external" | "unresolved";

/** A single path-like reference found in an asset's text, with its verdict. */
export interface AssetReference {
  /** File within the asset that contains the reference (relative to assetRoot, posix). */
  fromFile: string;
  /** Raw reference token exactly as written in the source. */
  raw: string;
  /** Path relative to assetRoot when it stays inside; the normalized join otherwise. */
  resolved: string;
  /** `true` when `resolved` names a directory on disk (vs a file). */
  isDir: boolean;
  classification: ReferenceClass;
}

/** A discovery warning emitted for an `uncovered-in-dir` reference. */
export interface ReferenceWarning {
  /** Relative path (from source root) of the manifest whose asset has the gap. */
  path: string;
  warning: string;
  /**
   * Optional machine-readable tag for advisory grouping. Consumers (e.g. the
   * sync layer) key off this instead of string-matching `warning`. Currently
   * only `"missing_sha256"` is emitted — one per asset with no pinned hash —
   * so the CLI can collapse many into a single summary line.
   */
  code?: string;
}

// Text file extensions we scan for references. Binary/code we don't parse for
// links — but we still resolve references that POINT at any extension.
const SCANNABLE_EXT = new Set([".md", ".mdx", ".markdown", ".txt"]);

// Reference tokens that are never local files.
function isNonLocal(token: string): boolean {
  return (
    token.length === 0 ||
    token.startsWith("#") ||
    token.startsWith("//") ||
    /^[a-z][a-z0-9+.-]*:/i.test(token) // http:, https:, mailto:, library:, etc.
  );
}

/**
 * Extract path-like candidate tokens from a chunk of markdown/text.
 *
 * Three sources, union-ed and de-duped:
 *   1. Markdown link / image targets — `[txt](path)` and `![alt](path)`.
 *   2. Inline code spans — `` `modes/write.md` `` (tables, prose).
 *   3. Bare path tokens — `scripts/run.ts` mentioned in running text.
 */
export function extractPathCandidates(text: string): string[] {
  const out = new Set<string>();

  // 1. Markdown links/images: capture the target up to whitespace or ')'.
  for (const m of text.matchAll(/!?\[[^\]]*\]\(\s*<?([^)>\s]+)>?(?:\s+"[^"]*")?\s*\)/g)) {
    if (m[1]) out.add(m[1]);
  }

  // 2. Inline code spans.
  for (const m of text.matchAll(/`([^`\n]+)`/g)) {
    const tok = m[1]?.trim();
    if (tok) out.add(tok);
  }

  // 3. Bare path tokens: at least one slash, path-ish characters, optionally a
  //    leading `./` or `../`. Trailing slash allowed (directory references).
  for (const m of text.matchAll(/(?:\.{0,2}\/)?(?:[\w.@-]+\/)+[\w.@-]*/g)) {
    if (m[0]) out.add(m[0]);
  }

  return [...out];
}

// Standalone single-segment `/<name>` slash command (Claude-Code cross-skill ref).
// Lookbehind/lookahead reject a slash preceded by, or a name followed by,
// `\w` `.` `/` `-` — so `path/x`, `/a/b`, `/foo.md`, and URLs never match.
const SLASH_COMMAND_RE = /(?<![\w./-])\/([a-z0-9][a-z0-9-]*)(?![\w./-])/g;

/**
 * Extract standalone single-segment slash-command names (without the leading
 * slash) from markdown/text — both prose (`Run a /grilling session`) and inline
 * code (`` `/grilling` ``). De-duped, first-seen order. Multi-segment paths
 * (`/api/users`), extensions (`/foo.md`), non-standalone slashes (`path/x`), and
 * URLs never match — those are path references handled by {@link scanAssetReferences}.
 */
export function extractSlashCommands(text: string): string[] {
  const out = new Set<string>();
  for (const m of text.matchAll(SLASH_COMMAND_RE)) {
    if (m[1]) out.add(m[1]);
  }
  return [...out];
}

/**
 * Scan an asset's scannable text files for standalone slash-command names,
 * unioned and de-duped. Shared by both discovery paths (local FS + virtual tree)
 * so the name-based body-link pass stays in strict parity.
 */
export function scanSlashCommandNames(afs: AssetFs, includedFiles: string[]): string[] {
  const out = new Set<string>();
  for (const f of includedFiles) {
    if (!SCANNABLE_EXT.has(path.posix.extname(f).toLowerCase())) continue;
    const text = afs.readText(f);
    if (text === null) continue;
    for (const name of extractSlashCommands(text)) out.add(name);
  }
  return [...out];
}

/**
 * Map each SKILL name to the ref(s) sharing it, for slash-command resolution.
 * Slash commands invoke skills, so only skills are candidate targets — a
 * same-named non-skill must not make a legit `/<name>`→skill edge ambiguous.
 * Shared by both discovery paths so the matching rule lives in one place.
 */
export function buildSkillNameToRefs(
  assets: ReadonlyArray<{ kind: string; name: string; ref: string }>,
): Map<string, string[]> {
  const nameToRefs = new Map<string, string[]>();
  for (const a of assets) {
    if (a.kind !== "skill") continue;
    const refs = nameToRefs.get(a.name);
    if (refs) refs.push(a.ref);
    else nameToRefs.set(a.name, [a.ref]);
  }
  return nameToRefs;
}

/** Resolve a slash-command name to a target ref, or undefined if ambiguous/self. */
export function resolveSlashCommand(
  nameToRefs: Map<string, string[]>,
  name: string,
  selfRef: string,
): string | undefined {
  const refs = nameToRefs.get(name);
  if (!refs || refs.length !== 1) return undefined; // unambiguous single match only
  return refs[0] === selfRef ? undefined : refs[0];
}

/** Append a deduped soft `body-link` edge keyed by `from`/`to`. */
export function pushBodyLinkEdge(
  edges: RequiresEdge[],
  seen: Set<string>,
  from: string,
  to: string,
): void {
  const key = `${from} ${to}`;
  if (seen.has(key)) return;
  seen.add(key);
  edges.push({ from, to, field: "body-link" });
}

/** A token is path-like if it has a slash or a short trailing file extension. */
function looksLikePath(token: string): boolean {
  if (isNonLocal(token)) return false;
  // Strip anchor/query so `modes/write.md#x` resolves to `modes/write.md`.
  const clean = token.split(/[#?]/, 1)[0] ?? token;
  if (clean.length === 0) return false;
  if (clean.includes("/")) return true;
  return /\.[a-z0-9]{1,6}$/i.test(clean);
}

/** Strip an anchor/query suffix and a single trailing slash. */
function cleanToken(token: string): string {
  const clean = (token.split(/[#?]/, 1)[0] ?? token).trim();
  return clean.endsWith("/") ? clean.slice(0, -1) : clean;
}

/**
 * Minimal filesystem view of a single asset, rooted at the asset directory.
 * Both discovery paths implement it so the scan/closure logic is shared
 * verbatim: {@link createNodeAssetFs} over `node:fs` (local) and the virtual
 * tree's blob index (store). All paths are posix, relative to the asset root.
 */
export interface AssetFs {
  /** Text content of a file, or `null` if absent/unreadable. */
  readText(rel: string): string | null;
  /** Classify a path: a file, a directory, or absent. */
  statKind(rel: string): "file" | "dir" | "absent";
  /** Files under `rel` (recursive), posix-relative to the root. A file `rel` yields `[rel]`. */
  listFilesUnder(rel: string): string[];
}

/** An {@link AssetFs} backed by the local filesystem under `assetRoot`. */
export function createNodeAssetFs(assetRoot: string): AssetFs {
  const listFilesUnder = (rel: string): string[] => {
    const full = path.join(assetRoot, rel);
    let stat: fs.Stats;
    try {
      stat = fs.statSync(full);
    } catch {
      return [];
    }
    if (!stat.isDirectory()) return [rel];
    const out: string[] = [];
    for (const entry of fs.readdirSync(full, { withFileTypes: true })) {
      const childRel = path.posix.join(rel, entry.name);
      if (entry.isDirectory()) out.push(...listFilesUnder(childRel));
      else if (entry.isFile()) out.push(childRel);
    }
    return out;
  };
  return {
    readText(rel) {
      try {
        return fs.readFileSync(path.join(assetRoot, rel), "utf-8");
      } catch {
        return null;
      }
    },
    statKind(rel) {
      try {
        return fs.statSync(path.join(assetRoot, rel)).isDirectory() ? "dir" : "file";
      } catch {
        return "absent";
      }
    },
    listFilesUnder,
  };
}

function toFs(assetRootOrFs: string | AssetFs): AssetFs {
  return typeof assetRootOrFs === "string" ? createNodeAssetFs(assetRootOrFs) : assetRootOrFs;
}

/**
 * Scan an asset's text files for path references and classify each one.
 *
 * @param assetRootOrFs  Absolute asset-root path, or an {@link AssetFs}.
 * @param includedFiles  Filter file list, relative to the asset root (posix).
 * @returns One {@link AssetReference} per distinct (fromFile, resolved) pair.
 */
export function scanAssetReferences(
  assetRootOrFs: string | AssetFs,
  includedFiles: string[],
): AssetReference[] {
  const afs = toFs(assetRootOrFs);
  const included = new Set(includedFiles);
  const isCoveredDir = (rel: string) => includedFiles.some((f) => f.startsWith(`${rel}/`));
  const results: AssetReference[] = [];
  const seen = new Set<string>();

  for (const fromFile of includedFiles) {
    if (!SCANNABLE_EXT.has(path.posix.extname(fromFile).toLowerCase())) continue;
    const text = afs.readText(fromFile);
    if (text === null) continue;

    const fromDir = path.posix.dirname(fromFile);
    for (const raw of extractPathCandidates(text)) {
      if (!looksLikePath(raw)) continue;
      const clean = cleanToken(raw);
      if (clean.length === 0) continue;

      // Resolve relative to the referencing file's directory, posix-normalized.
      const joined = path.posix.normalize(
        fromDir === "." ? clean : path.posix.join(fromDir, clean),
      );

      const dedup = `${fromFile} ${joined}`;
      if (seen.has(dedup)) continue;
      seen.add(dedup);

      // Escapes the asset root → another asset or a repo-level file.
      if (joined === ".." || joined.startsWith("../")) {
        results.push({ fromFile, raw, resolved: joined, isDir: false, classification: "external" });
        continue;
      }

      // Does it exist inside the asset?
      const kind = afs.statKind(joined);
      if (kind === "absent") {
        results.push({
          fromFile,
          raw,
          resolved: joined,
          isDir: false,
          classification: "unresolved",
        });
        continue;
      }

      const isDir = kind === "dir";
      const covered = isDir ? isCoveredDir(joined) : included.has(joined);
      results.push({
        fromFile,
        raw,
        resolved: joined,
        isDir,
        classification: covered ? "covered" : "uncovered-in-dir",
      });
    }
  }

  return results;
}

/**
 * Result of a reference-closure expansion: the final file set plus the paths
 * that were pulled in beyond the structural seed (for observability).
 */
export interface ClosureResult {
  /** Final file list (relative to the asset root, sorted). */
  files: string[];
  /** Files added by closure that the structural filter had dropped. */
  added: string[];
}

/**
 * Expand a structural file set to its reference closure: repeatedly scan the
 * asset's text files, and for every `uncovered-in-dir` reference pull the
 * target file (or whole directory) into the set, then re-scan newly added text
 * files — to a fixpoint. Pure in-asset expansion; never escapes the root.
 *
 * @param assetRootOrFs  Absolute asset-root path, or an {@link AssetFs}.
 * @param seedFiles      Structural filter file list, relative to the root.
 */
export function closeOverReferences(
  assetRootOrFs: string | AssetFs,
  seedFiles: string[],
): ClosureResult {
  const afs = toFs(assetRootOrFs);
  const included = new Set(seedFiles);
  const added = new Set<string>();

  for (;;) {
    let grew = false;
    for (const r of scanAssetReferences(afs, [...included])) {
      if (r.classification !== "uncovered-in-dir") continue;
      for (const f of afs.listFilesUnder(r.resolved)) {
        if (!included.has(f)) {
          included.add(f);
          added.add(f);
          grew = true;
        }
      }
    }
    if (!grew) break;
  }

  return { files: [...included].sort(), added: [...added].sort() };
}

/**
 * Lint an asset: return one warning per distinct uncovered in-dir reference
 * target. The structural filter dropped these bytes even though the asset's
 * own text points at them.
 *
 * @param manifestRelPath  Manifest path from source root (for the warning).
 * @param assetRootOrFs    Absolute asset-root path, or an {@link AssetFs}.
 * @param includedFiles    Filter file list, relative to the asset root.
 */
export function lintUncoveredReferences(
  manifestRelPath: string,
  assetRootOrFs: string | AssetFs,
  includedFiles: string[],
): ReferenceWarning[] {
  const refs = scanAssetReferences(assetRootOrFs, includedFiles);
  const targets = new Map<string, string>(); // resolved → raw (first seen)
  for (const r of refs) {
    if (r.classification === "uncovered-in-dir" && !targets.has(r.resolved)) {
      targets.set(r.resolved, r.raw);
    }
  }
  return [...targets.entries()].map(([resolved, raw]) => ({
    path: manifestRelPath,
    warning: `references "${raw}" → ${resolved}, which exists in the asset directory but is not captured by the file filter`,
  }));
}
